/**
 * QC (08 + 00c) i18n — FR default + AR (RTL). Registered once in the module's onRegister(). Keys are
 * namespaced `qc.*` and `nav.qc.*`; missing keys fall back to FR then the raw key (never a crash, never
 * a blank). Layout mirrors automatically under dir="rtl" via CSS logical properties; codes / values /
 * z-scores stay LTR with .nv-ltr-island. Clinical / verdict semantics always pair color + glyph + text.
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // nav
  "nav.section.qc": "Contrôle qualité",
  "nav.qc.lots": "Lots de contrôle",
  "nav.qc.chart": "Levey-Jennings",
  "nav.qc.observation": "Saisie contrôle",
  "nav.qc.holds": "Rétentions QC",

  // shared
  "qc.action.refresh": "Actualiser",
  "qc.action.cancel": "Annuler",
  "qc.action.close": "Fermer",
  "qc.field.required": "Champ requis.",
  "qc.field.numInvalid": "Valeur numérique invalide.",
  "qc.field.sdInvalid": "L'écart-type doit être strictement positif.",
  "qc.field.dateInvalid": "Date invalide.",
  "qc.field.optional": "facultatif",
  "qc.parametre": "Paramètre",
  "qc.parametre.placeholder": "Ex. GLU, NA, K…",
  "qc.automate": "Automate",
  "qc.automate.placeholder": "ID automate (facultatif)",
  "qc.automate.all": "Tous automates",
  "qc.niveau": "Niveau",
  "qc.niveau.niveau_1": "Niveau 1",
  "qc.niveau.niveau_2": "Niveau 2",
  "qc.niveau.niveau_3": "Niveau 3",

  // module-absent (cloud / unmounted)
  "qc.absent.title": "Module non disponible",
  "qc.absent.desc":
    "Le contrôle qualité n'est pas activé sur cette installation. Contactez votre administrateur si vous attendiez cet écran.",

  // verdicts
  "qc.verdict.accepte": "Accepté",
  "qc.verdict.rejete": "Rejeté",
  "qc.verdict.alerte": "Alerte",
  "qc.verdict.en_cours": "En cours",
  "qc.verdict.unknown": "Inconnu",

  // ── Lots screen ────────────────────────────────────────────────────────────────
  "qc.lots.title": "Lots de contrôle",
  "qc.lots.subtitle": "Enrôlement, validation (gel des statistiques) et péremption des lots QC.",
  "qc.lots.new": "Nouveau lot",
  "qc.lots.sweep": "Balayer les périmés",
  "qc.lots.empty.title": "Aucun lot de contrôle",
  "qc.lots.empty.desc": "Enrôlez un premier lot pour démarrer le suivi Levey-Jennings.",
  "qc.lots.col.code": "Code",
  "qc.lots.col.parametre": "Paramètre",
  "qc.lots.col.niveau": "Niveau",
  "qc.lots.col.cible": "Cible",
  "qc.lots.col.sd": "Écart-type",
  "qc.lots.col.peremption": "Péremption",
  "qc.lots.col.statut": "Statut",
  "qc.lots.col.actions": "Actions",
  "qc.lots.action.valider": "Valider",
  "qc.lots.statut.brouillon": "Brouillon",
  "qc.lots.statut.valide": "Validé",
  "qc.lots.statut.expire": "Périmé",
  "qc.lot.statut.brouillon": "Brouillon",
  "qc.lot.statut.valide": "Validé",
  "qc.lot.statut.expire": "Périmé",
  "qc.lot.statut.unknown": "—",

  // enrol modal
  "qc.lots.enrol.title": "Enrôler un lot de contrôle",
  "qc.lots.enrol.submit": "Enrôler le lot",
  "qc.lots.enrol.success": "Lot enrôlé (brouillon).",
  "qc.lots.field.code": "Code lot",
  "qc.lots.field.code.hint": "Référence du fabricant (ex. LOT-2024-A).",
  "qc.lots.field.fabricant": "Fabricant",
  "qc.lots.field.unite": "Unité",
  "qc.lots.field.peremption": "Date de péremption",
  "qc.lots.field.cible": "Cible (moyenne)",
  "qc.lots.field.cible.hint": "Facultatif au brouillon — figée à la validation.",
  "qc.lots.field.sd": "Écart-type (SD)",
  "qc.lots.field.sd.hint": "Facultatif au brouillon — doit être > 0 à la validation.",

  // valider modal
  "qc.lots.valider.title": "Valider le lot — geler les statistiques",
  "qc.lots.valider.warn":
    "La validation FIGE la cible et l'écart-type. Une recalibration = un nouveau lot, jamais une modification en place (médico-légal).",
  "qc.lots.valider.submit": "Valider et geler",
  "qc.lots.valider.success": "Lot validé — statistiques figées.",

  // sweep modal
  "qc.lots.sweep.title": "Balayer les lots périmés",
  "qc.lots.sweep.warn":
    "Cette opération fait passer tout lot validé dont la date est dépassée vers « périmé ». Opération idempotente et auditée.",
  "qc.lots.sweep.submit": "Lancer le balayage",
  "qc.lots.sweep.success.none": "Aucun lot à balayer.",
  "qc.lots.sweep.success.some": "{n} lot(s) marqué(s) périmé(s).",

  // ── Chart (Levey-Jennings) screen ─────────────────────────────────────────────────
  "qc.chart.title": "Carte de Levey-Jennings",
  "qc.chart.subtitle": "Série QC chronologique, bandes ±1/2/3 SD et violations Westgard par point.",
  "qc.chart.filter.run": "Afficher la série",
  "qc.chart.lot": "Lot de contrôle",
  "qc.chart.lot.all": "Tous lots validés",
  "qc.chart.prompt.title": "Choisissez un paramètre",
  "qc.chart.prompt.desc": "Saisissez un code paramètre pour afficher sa série Levey-Jennings.",
  "qc.chart.empty.title": "Aucun point",
  "qc.chart.empty.desc": "Aucune observation QC pour ce filtre. Saisissez un contrôle pour démarrer la série.",
  "qc.chart.nobands": "Bandes indisponibles (aucun lot validé pour cette série).",
  "qc.chart.axis.value": "Valeur",
  "qc.chart.axis.point": "Contrôle",
  "qc.chart.legend.cible": "Cible",
  "qc.chart.legend.sd1": "±1 SD",
  "qc.chart.legend.sd2": "±2 SD",
  "qc.chart.legend.sd3": "±3 SD",
  "qc.chart.legend.point": "Observation",
  "qc.chart.legend.violation": "Violation",
  "qc.chart.tip.value": "Valeur",
  "qc.chart.tip.z": "z",
  "qc.chart.tip.rules": "Règles",
  "qc.chart.points.title": "Points de la série",
  "qc.chart.col.at": "Horodatage",
  "qc.chart.col.valeur": "Valeur",
  "qc.chart.col.z": "z-score",
  "qc.chart.col.verdict": "Verdict",
  "qc.chart.col.rules": "Règles violées",
  "qc.chart.rules.none": "—",

  // ── Observation entry screen ──────────────────────────────────────────────────────
  "qc.obs.title": "Saisie d'un contrôle",
  "qc.obs.subtitle": "Enregistre une observation QC et déclenche l'auto-évaluation Westgard.",
  "qc.obs.field.valeur": "Valeur observée",
  "qc.obs.field.valeur.hint": "Mesure du matériel de contrôle.",
  "qc.obs.submit": "Enregistrer le contrôle",
  "qc.obs.success.accepte": "Contrôle accepté (z = {z}).",
  "qc.obs.success.rejete": "Contrôle rejeté — rétention ouverte (z = {z}).",
  "qc.obs.success.alerte": "Contrôle en alerte (z = {z}).",
  "qc.obs.success.generic": "Contrôle enregistré (z = {z}).",
  "qc.obs.degraded.title": "Évaluation impossible",
  "qc.obs.degraded.no_valide_lot":
    "Aucun lot de contrôle validé pour ce paramètre/niveau. Validez un lot avant de saisir un contrôle.",
  "qc.obs.degraded.ecart_type_invalide":
    "L'écart-type du lot actif est invalide (≤ 0). Recalibrez un nouveau lot.",
  "qc.obs.result.title": "Dernier résultat",
  "qc.obs.result.z": "z-score",
  "qc.obs.result.verdict": "Verdict",
  "qc.obs.result.rules": "Règles violées",
  "qc.obs.result.hold": "Rétention ouverte",
  "qc.obs.result.hold.yes": "Oui",
  "qc.obs.result.hold.no": "Non",

  // ── Holds (rétentions) worklist screen ────────────────────────────────────────────
  "qc.holds.title": "Rétentions QC",
  "qc.holds.subtitle": "Liste des rétentions ouvertes (estRetenu) — levée par le biologiste.",
  "qc.holds.new": "Nouvelle rétention",
  "qc.holds.empty.title": "Aucune rétention ouverte",
  "qc.holds.empty.desc": "Aucun paramètre n'est actuellement retenu. La validation des résultats n'est pas bloquée.",
  "qc.holds.col.parametre": "Paramètre",
  "qc.holds.col.automate": "Automate",
  "qc.holds.col.debut": "Début fenêtre",
  "qc.holds.col.fin": "Fin fenêtre",
  "qc.holds.col.motif": "Motif",
  "qc.holds.col.actions": "Actions",
  "qc.holds.automate.all": "Tous automates",
  "qc.holds.action.lever": "Lever",
  "qc.holds.lever.title": "Lever la rétention",
  "qc.holds.lever.confirm": "Confirmez la levée de la rétention sur « {parametre} ». La trace médico-légale est conservée.",
  "qc.holds.lever.submit": "Lever la rétention",
  "qc.holds.lever.success": "Rétention levée.",

  // open hold modal
  "qc.holds.open.title": "Ouvrir une rétention manuelle",
  "qc.holds.open.submit": "Ouvrir la rétention",
  "qc.holds.open.success": "Rétention ouverte.",
  "qc.holds.field.debut": "Début de fenêtre",
  "qc.holds.field.fin": "Fin de fenêtre",
  "qc.holds.field.motif": "Motif",
  "qc.holds.field.motif.hint": "Justification (médico-légale, ≥ 2 caractères).",
};

const AR: Record<string, string> = {
  // nav
  "nav.section.qc": "مراقبة الجودة",
  "nav.qc.lots": "دفعات المراقبة",
  "nav.qc.chart": "ليفي-جينينغز",
  "nav.qc.observation": "إدخال مراقبة",
  "nav.qc.holds": "حجوزات الجودة",

  // shared
  "qc.action.refresh": "تحديث",
  "qc.action.cancel": "إلغاء",
  "qc.action.close": "إغلاق",
  "qc.field.required": "حقل مطلوب.",
  "qc.field.numInvalid": "قيمة رقمية غير صالحة.",
  "qc.field.sdInvalid": "يجب أن يكون الانحراف المعياري موجبًا تمامًا.",
  "qc.field.dateInvalid": "تاريخ غير صالح.",
  "qc.field.optional": "اختياري",
  "qc.parametre": "المُعامِل",
  "qc.parametre.placeholder": "مثال GLU، NA، K…",
  "qc.automate": "الجهاز",
  "qc.automate.placeholder": "معرّف الجهاز (اختياري)",
  "qc.automate.all": "كل الأجهزة",
  "qc.niveau": "المستوى",
  "qc.niveau.niveau_1": "المستوى 1",
  "qc.niveau.niveau_2": "المستوى 2",
  "qc.niveau.niveau_3": "المستوى 3",

  // module-absent
  "qc.absent.title": "الوحدة غير متوفرة",
  "qc.absent.desc":
    "مراقبة الجودة غير مفعّلة على هذا التثبيت. تواصل مع المسؤول إذا كنت تتوقع هذه الشاشة.",

  // verdicts
  "qc.verdict.accepte": "مقبول",
  "qc.verdict.rejete": "مرفوض",
  "qc.verdict.alerte": "تنبيه",
  "qc.verdict.en_cours": "قيد التنفيذ",
  "qc.verdict.unknown": "غير معروف",

  // Lots
  "qc.lots.title": "دفعات المراقبة",
  "qc.lots.subtitle": "تسجيل الدفعات والتحقق منها (تجميد الإحصاءات) وانتهاء صلاحيتها.",
  "qc.lots.new": "دفعة جديدة",
  "qc.lots.sweep": "كنس المنتهية",
  "qc.lots.empty.title": "لا توجد دفعة مراقبة",
  "qc.lots.empty.desc": "سجّل دفعة أولى لبدء متابعة ليفي-جينينغز.",
  "qc.lots.col.code": "الرمز",
  "qc.lots.col.parametre": "المُعامِل",
  "qc.lots.col.niveau": "المستوى",
  "qc.lots.col.cible": "الهدف",
  "qc.lots.col.sd": "الانحراف المعياري",
  "qc.lots.col.peremption": "انتهاء الصلاحية",
  "qc.lots.col.statut": "الحالة",
  "qc.lots.col.actions": "إجراءات",
  "qc.lots.action.valider": "تحقّق",
  "qc.lots.statut.brouillon": "مسودة",
  "qc.lots.statut.valide": "مُتحقق",
  "qc.lots.statut.expire": "منتهٍ",
  "qc.lot.statut.brouillon": "مسودة",
  "qc.lot.statut.valide": "مُتحقق",
  "qc.lot.statut.expire": "منتهٍ",
  "qc.lot.statut.unknown": "—",

  "qc.lots.enrol.title": "تسجيل دفعة مراقبة",
  "qc.lots.enrol.submit": "تسجيل الدفعة",
  "qc.lots.enrol.success": "تم تسجيل الدفعة (مسودة).",
  "qc.lots.field.code": "رمز الدفعة",
  "qc.lots.field.code.hint": "مرجع الصانع (مثال LOT-2024-A).",
  "qc.lots.field.fabricant": "الصانع",
  "qc.lots.field.unite": "الوحدة",
  "qc.lots.field.peremption": "تاريخ انتهاء الصلاحية",
  "qc.lots.field.cible": "الهدف (المتوسط)",
  "qc.lots.field.cible.hint": "اختياري في المسودة — يُجمّد عند التحقق.",
  "qc.lots.field.sd": "الانحراف المعياري",
  "qc.lots.field.sd.hint": "اختياري في المسودة — يجب أن يكون > 0 عند التحقق.",

  "qc.lots.valider.title": "التحقق من الدفعة — تجميد الإحصاءات",
  "qc.lots.valider.warn":
    "يُجمّد التحقق الهدف والانحراف المعياري. إعادة المعايرة = دفعة جديدة، وليس تعديلًا في المكان (طبي-قانوني).",
  "qc.lots.valider.submit": "تحقّق وجمّد",
  "qc.lots.valider.success": "تم التحقق من الدفعة — الإحصاءات مُجمّدة.",

  "qc.lots.sweep.title": "كنس الدفعات المنتهية",
  "qc.lots.sweep.warn": "تنقل هذه العملية كل دفعة متحقّقة تجاوزت تاريخها إلى « منتهٍ ». عملية متكافئة ومدققة.",
  "qc.lots.sweep.submit": "بدء الكنس",
  "qc.lots.sweep.success.none": "لا توجد دفعة للكنس.",
  "qc.lots.sweep.success.some": "تم وسم {n} دفعة كمنتهية.",

  // Chart
  "qc.chart.title": "مخطط ليفي-جينينغز",
  "qc.chart.subtitle": "سلسلة مراقبة زمنية، نطاقات ±1/2/3 SD وانتهاكات ويستغارد لكل نقطة.",
  "qc.chart.filter.run": "عرض السلسلة",
  "qc.chart.lot": "دفعة المراقبة",
  "qc.chart.lot.all": "كل الدفعات المتحققة",
  "qc.chart.prompt.title": "اختر مُعامِلًا",
  "qc.chart.prompt.desc": "أدخل رمز المُعامِل لعرض سلسلة ليفي-جينينغز.",
  "qc.chart.empty.title": "لا توجد نقاط",
  "qc.chart.empty.desc": "لا توجد مراقبة لهذا المرشّح. أدخل مراقبة لبدء السلسلة.",
  "qc.chart.nobands": "النطاقات غير متوفرة (لا توجد دفعة متحقّقة لهذه السلسلة).",
  "qc.chart.axis.value": "القيمة",
  "qc.chart.axis.point": "المراقبة",
  "qc.chart.legend.cible": "الهدف",
  "qc.chart.legend.sd1": "±1 SD",
  "qc.chart.legend.sd2": "±2 SD",
  "qc.chart.legend.sd3": "±3 SD",
  "qc.chart.legend.point": "ملاحظة",
  "qc.chart.legend.violation": "انتهاك",
  "qc.chart.tip.value": "القيمة",
  "qc.chart.tip.z": "z",
  "qc.chart.tip.rules": "القواعد",
  "qc.chart.points.title": "نقاط السلسلة",
  "qc.chart.col.at": "الطابع الزمني",
  "qc.chart.col.valeur": "القيمة",
  "qc.chart.col.z": "z-score",
  "qc.chart.col.verdict": "الحكم",
  "qc.chart.col.rules": "القواعد المنتهَكة",
  "qc.chart.rules.none": "—",

  // Observation
  "qc.obs.title": "إدخال مراقبة",
  "qc.obs.subtitle": "يسجّل ملاحظة مراقبة ويُطلق التقييم التلقائي ويستغارد.",
  "qc.obs.field.valeur": "القيمة المُلاحَظة",
  "qc.obs.field.valeur.hint": "قياس مادة المراقبة.",
  "qc.obs.submit": "تسجيل المراقبة",
  "qc.obs.success.accepte": "مراقبة مقبولة (z = {z}).",
  "qc.obs.success.rejete": "مراقبة مرفوضة — فُتحت حجز (z = {z}).",
  "qc.obs.success.alerte": "مراقبة في تنبيه (z = {z}).",
  "qc.obs.success.generic": "تم تسجيل المراقبة (z = {z}).",
  "qc.obs.degraded.title": "تعذّر التقييم",
  "qc.obs.degraded.no_valide_lot":
    "لا توجد دفعة مراقبة متحقّقة لهذا المُعامِل/المستوى. تحقّق من دفعة قبل إدخال المراقبة.",
  "qc.obs.degraded.ecart_type_invalide":
    "الانحراف المعياري للدفعة النشطة غير صالح (≤ 0). أعد معايرة دفعة جديدة.",
  "qc.obs.result.title": "آخر نتيجة",
  "qc.obs.result.z": "z-score",
  "qc.obs.result.verdict": "الحكم",
  "qc.obs.result.rules": "القواعد المنتهَكة",
  "qc.obs.result.hold": "حجز مفتوح",
  "qc.obs.result.hold.yes": "نعم",
  "qc.obs.result.hold.no": "لا",

  // Holds
  "qc.holds.title": "حجوزات الجودة",
  "qc.holds.subtitle": "قائمة الحجوزات المفتوحة (estRetenu) — يرفعها البيولوجي.",
  "qc.holds.new": "حجز جديد",
  "qc.holds.empty.title": "لا توجد حجوزات مفتوحة",
  "qc.holds.empty.desc": "لا يوجد مُعامِل محجوز حاليًا. التحقق من النتائج غير محظور.",
  "qc.holds.col.parametre": "المُعامِل",
  "qc.holds.col.automate": "الجهاز",
  "qc.holds.col.debut": "بداية النافذة",
  "qc.holds.col.fin": "نهاية النافذة",
  "qc.holds.col.motif": "السبب",
  "qc.holds.col.actions": "إجراءات",
  "qc.holds.automate.all": "كل الأجهزة",
  "qc.holds.action.lever": "رفع",
  "qc.holds.lever.title": "رفع الحجز",
  "qc.holds.lever.confirm": "أكّد رفع الحجز عن « {parametre} ». يُحتفظ بالأثر الطبي-القانوني.",
  "qc.holds.lever.submit": "رفع الحجز",
  "qc.holds.lever.success": "تم رفع الحجز.",

  "qc.holds.open.title": "فتح حجز يدوي",
  "qc.holds.open.submit": "فتح الحجز",
  "qc.holds.open.success": "تم فتح الحجز.",
  "qc.holds.field.debut": "بداية النافذة",
  "qc.holds.field.fin": "نهاية النافذة",
  "qc.holds.field.motif": "السبب",
  "qc.holds.field.motif.hint": "تبرير (طبي-قانوني، ≥ حرفان).",
};

/** Register the qc FR/AR strings (idempotent). Called once from the module's onRegister(). */
export function registerQcMessages(): void {
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
